import OneBallotResource from '../resources/Ballot/One';
import {
  getHasStartedBallot,
  getIncompleteCategoryKeys,
  getIsBallotComplete,
} from '../resources/Ballot/services';
import ballotDisplayOrder from '../resources/Ballot/constants/ballotDisplayOrder';
import ObjectiveStateResource from '../resources/ObjectiveState';
import { makeClassName } from '@siteless/util';
import pluralize from '../util/pluralize';
import useCopy from '../util/useCopy';
import { Header } from './Header';
import ArrowRightIcon from './ArrowRightIcon';
import Button from './Button';
import { BallotPreviewItem } from './BallotPreviewItem';
import { FavoritesBar } from './FavoritesBar';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import ballotOptionTypeOrder from '../resources/Ballot/constants/ballotOptionTypeOrder';
import optionTypes from '../resources/Ballot/constants/optionTypes';
import { LetterboxdBar } from './LetterboxdBar';

export let ComposeBallotIndex = ({
  onFocusItem,
}: {
  onFocusItem: (itemKey: (typeof ballotDisplayOrder)[number]) => void;
}) => {
  let copy = useCopy();
  let currentUser = CurrentUserMustResource.use();
  let ballot = OneBallotResource.use(currentUser.id);
  let objectiveState = ObjectiveStateResource.use();
  let { ballotShape } = objectiveState;

  let hasStartedBallot = getHasStartedBallot(ballot);
  let isBallotComplete = getIsBallotComplete({ ballot, ballotShape });

  let incompleteCategoryKeys = getIncompleteCategoryKeys({
    ballot,
    ballotShape,
  });

  return (
    <div className="px-6 pt-4 pb-6">
      <Header className="mb-4">{copy.yourBallot}</Header>
      {!isBallotComplete && (
        <>
          <div className="text-rose-bright mb-2">
            {pluralize({
              singular: copy.category,
              plural: copy.categories,
              count: incompleteCategoryKeys.length,
            })}{' '}
            {copy.toGo}.
          </div>
          <div
            className={makeClassName(
              'text-md mb-6 flex flex-col gap-2 rounded-lg border-4 border-neutral-100 px-3 pt-2',
              hasStartedBallot && 'max-h-[30vh] overflow-auto',
            )}
          >
            {ballotDisplayOrder
              .filter((key) => incompleteCategoryKeys.includes(key))
              .filter((ballotItemKey) => ballotShape[ballotItemKey])
              .map((ballotItemKey, index) => {
                let { displayName, options } = ballotShape[ballotItemKey];

                let optionKeys = Object.entries(options)
                  .sort(
                    (a, b) =>
                      ballotOptionTypeOrder.findIndex(
                        (item) => item === a[1].type,
                      ) -
                      ballotOptionTypeOrder.findIndex(
                        (item) => item === b[1].type,
                      ),
                  )
                  .map(([key]) => key);
                return (
                  <div
                    className="flex flex-none items-center justify-between truncate"
                    key={ballotItemKey}
                    onClick={() => {
                      onFocusItem(ballotItemKey);
                    }}
                  >
                    {index === 0 ? (
                      <div className="my-1 w-full">
                        <div className="mb-1 flex justify-between">
                          <div className="font-medium">{displayName}</div>
                          <ArrowRightIcon className="h-5 text-neutral-300" />
                        </div>
                        <div
                          className="flex w-full items-start gap-1 overflow-hidden"
                          style={{
                            maskImage: `linear-gradient(
                              to right,
                              rgba(0, 0, 0, 1) calc(100% - 20px),
                              rgba(0, 0, 0, 0.7) calc(100% - 10px),
                              transparent
                            )`,
                          }}
                        >
                          {optionKeys.map((optionKey) => {
                            let { data, type: typeKey } = options[optionKey];
                            let type = optionTypes[typeKey];
                            let optionComponentProps =
                              type.getOptionComponentProps({
                                // @ts-expect-error TODO
                                data,
                                copy,
                                objectiveState,
                              });

                            let { title, posterImageUrl } =
                              optionComponentProps;

                            let graphic = posterImageUrl ? (
                              <img
                                src={posterImageUrl}
                                className="h-full w-full object-cover"
                                alt={title}
                              />
                            ) : (
                              <div className="bg-neutral-0 flex h-full w-full items-center justify-center">
                                <type.Icon />
                              </div>
                            );

                            return (
                              <div
                                key={optionKey}
                                className="h-14 w-14 flex-none overflow-hidden rounded-full border-4 border-neutral-100"
                              >
                                <div className="border-neutral-0 h-full w-full overflow-hidden rounded-full border-2">
                                  {graphic}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <Button
                          className="mt-3 w-full"
                          onClick={() => {
                            onFocusItem(ballotItemKey);
                          }}
                        >
                          {hasStartedBallot ? copy._continue : copy.getStarted}
                        </Button>
                      </div>
                    ) : (
                      <>
                        {displayName}
                        <ArrowRightIcon className="h-5 text-neutral-300" />
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}
      {isBallotComplete && currentUser.letterboxdId && (
        <LetterboxdBar
          userId={currentUser.id}
          letterboxdId={currentUser.letterboxdId}
          className="mb-4"
        />
      )}
      {currentUser.handle && (
        <FavoritesBar
          userId={currentUser.id}
          userHandle={currentUser.handle}
          className="mb-6"
        />
      )}
      {ballotDisplayOrder
        .filter((key) => !incompleteCategoryKeys.includes(key))
        .map((ballotItemKey) => {
          let { displayName, options } =
            objectiveState.ballotShape[ballotItemKey];
          let selectedOption =
            options[(ballot as NonNullable<typeof ballot>)[ballotItemKey]];

          return (
            <BallotPreviewItem
              key={ballotItemKey}
              ballotItemDisplayName={displayName}
              onClick={() => {
                onFocusItem(ballotItemKey);
              }}
              selectedOption={selectedOption}
              isEditable
            />
          );
        })}
    </div>
  );
};
